/**
 * api-errors.js · PR-IS-CIERRE-PRG-1 (Ann 2026-05-24)
 *
 * Helper compartido para extraer mensajes legibles de errores axios cuando
 * el backend devuelve `detail` como objeto (patrón {code, detail: {...}}
 * usado por los orquestadores atómicos IS: cerrar_incidencia_salud,
 * programar_cierre_incidencia, aplicar_cierre_programado_incidencia,
 * activar_is_programada, editar_fechas_incidencia, etc.).
 *
 * Antes:
 *   toast.error(err.response?.data?.detail || "Error...")
 *   ← cuando detail es objeto → renderiza [object Object] o nada
 *
 * Después:
 *   toast.error(extractApiErrorMessage(err) || "Error...")
 *   ← siempre string legible · "CODE · razón humana"
 */

export function extractApiErrorMessage(err, fallback = null) {
  if (!err) return fallback;
  const detail = err?.response?.data?.detail;
  if (!detail) {
    return err?.message || fallback;
  }
  // string plano → devolver tal cual
  if (typeof detail === "string") return detail;
  // objeto con shape { code, detail: {razon|...} | string }
  if (typeof detail === "object") {
    const code = detail.code || null;
    const inner = detail.detail;
    // detail.detail puede ser string, objeto con .razon, o null
    let texto = null;
    if (typeof inner === "string") {
      texto = inner;
    } else if (inner && typeof inner === "object") {
      texto = inner.razon || inner.message || null;
      if (!texto) {
        // último recurso: pares clave/valor compactos (sin objetos anidados)
        try {
          const parts = [];
          for (const [k, v] of Object.entries(inner)) {
            if (v === null || typeof v === "object") continue;
            parts.push(`${k}=${v}`);
          }
          if (parts.length) texto = parts.join(" · ");
        } catch (_) {
          /* noop */
        }
      }
    }
    if (code && texto) return `${code} · ${texto}`;
    if (code) return code;
    if (texto) return texto;
    // Fallback inseguro: JSON.stringify compacto y truncado.
    try {
      const json = JSON.stringify(detail);
      return json.length > 300 ? json.slice(0, 297) + "..." : json;
    } catch (_) {
      return fallback;
    }
  }
  return fallback;
}
